import React, { useState, useCallback, useMemo } from 'react';
import {
  CategoryTreeDisplayBox,
  CategoryTreeItemsWrap,
  CategoryTreeItem,
  TreeConnector,
  CategoryTreeLabel,
  CategoryEditBtn,
  CategoryEmptyBtn,
  CategorySelectorWrap,
  CategoryBreadcrumb,
  BreadcrumbBackBtn,
  BreadcrumbPath,
  CategoryOptionsWrap,
  CategoryOptionBtn,
  CategoryOptionLabel,
  CategoryCancelBtn,
} from '../styled-comps';
import Icon from './icon';

// Walk the tree to find a node by its dot-separated path
const findNodeByPath = (tree, path) => {
  if (!tree || !path) return null;
  for (const node of tree) {
    if (node.path === path) return node;
    if (path.startsWith(node.path + '.') && node.children?.length) {
      const found = findNodeByPath(node.children, path);
      if (found) return found;
    }
  }
  return null;
};

// Build display segments for the selected path
const getDisplaySegments = (tree, path) => {
  if (!path || !tree?.length) return [];
  const parts = path.split('.');
  const segments = [];
  let currentPath = '';

  for (let i = 0; i < parts.length; i++) {
    currentPath = currentPath ? `${currentPath}.${parts[i]}` : parts[i];
    const node = findNodeByPath(tree, currentPath);
    segments.push({
      name: node?.display_name || parts[i].replace(/_/g, ' '),
      path: currentPath,
    });
  }
  return segments;
};

// Get children for the current navigation position
const getCurrentOptions = (tree, navPath) => {
  if (!navPath || !navPath.length) return tree || [];
  const currentNode = findNodeByPath(tree, navPath[navPath.length - 1]);
  return currentNode?.children || [];
};

// ── Display Mode ──────────────────────────────────────────────────────

const TreeDisplay = ({ segments, onEdit }) => (
  <CategoryTreeDisplayBox $showingCategoryTree={segments.length > 1}>
    <CategoryTreeItemsWrap>
      {segments.map((seg, idx) => {
        const isLast = idx === segments.length - 1;
        const indent = idx > 1 ? (idx - 1) * 21 : 0;
        return (
          <CategoryTreeItem key={seg.path} style={{ paddingLeft: indent }}>
            {idx > 0 && <TreeConnector>└─</TreeConnector>}
            <CategoryTreeLabel $isLast={isLast}>{seg.name}</CategoryTreeLabel>
          </CategoryTreeItem>
        );
      })}
    </CategoryTreeItemsWrap>
    <CategoryEditBtn onClick={onEdit}>
      <Icon name="setting" size={12} />
    </CategoryEditBtn>
  </CategoryTreeDisplayBox>
);

// ── Selector Mode ─────────────────────────────────────────────────────

const TreeSelector = ({ tree, selectedPath, onSelect, onCancel }) => {
  const [navPath, setNavPath] = useState([]);

  const options = useMemo(() => getCurrentOptions(tree, navPath), [tree, navPath]);

  const breadcrumbSegments = useMemo(() => {
    return navPath.map(p => {
      const node = findNodeByPath(tree, p);
      return {
        name:
          node?.display_name ||
          p
            .split('.')
            .pop()
            .replace(/_/g, ' '),
        path: p,
      };
    });
  }, [tree, navPath]);

  const handleOptionClick = useCallback(
    node => {
      if (node.is_leaf) {
        onSelect(node.path);
      } else {
        setNavPath(prev => [...prev, node.path]);
      }
    },
    [onSelect],
  );

  const handleBack = useCallback(() => {
    setNavPath(prev => prev.slice(0, -1));
  }, []);

  return (
    <CategorySelectorWrap>
      {navPath.length > 0 && (
        <CategoryBreadcrumb>
          <BreadcrumbBackBtn onClick={handleBack}>← Back</BreadcrumbBackBtn>
          <BreadcrumbPath>
            {breadcrumbSegments.map((seg, idx) => (
              <span key={seg.path}>
                {idx > 0 && ' › '}
                {seg.name}
              </span>
            ))}
          </BreadcrumbPath>
        </CategoryBreadcrumb>
      )}

      <CategoryOptionsWrap>
        {options.map(node => (
          <CategoryOptionBtn
            key={node.path}
            $selected={node.path === selectedPath}
            onClick={() => handleOptionClick(node)}
          >
            <CategoryOptionLabel>{node.display_name}</CategoryOptionLabel>
            {!node.is_leaf && <Icon name="chev" size={12} />}
          </CategoryOptionBtn>
        ))}
      </CategoryOptionsWrap>

      <CategoryCancelBtn onClick={onCancel}>Cancel</CategoryCancelBtn>
    </CategorySelectorWrap>
  );
};

const CategoryTree = ({ selectedPath, onSelect, categoryTree }) => {
  const [isEditing, setIsEditing] = useState(false);

  const segments = useMemo(() => getDisplaySegments(categoryTree, selectedPath), [
    categoryTree,
    selectedPath,
  ]);

  const handleSelect = useCallback(
    path => {
      setIsEditing(false);
      onSelect(path);
    },
    [onSelect],
  );

  if (isEditing) {
    return (
      <TreeSelector
        tree={categoryTree}
        selectedPath={selectedPath}
        onSelect={handleSelect}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  if (!selectedPath || segments.length === 0) {
    return (
      <CategoryEmptyBtn onClick={() => setIsEditing(true)}>
        Select category
        <Icon name="chev" size={12} />
      </CategoryEmptyBtn>
    );
  }

  return <TreeDisplay segments={segments} onEdit={() => setIsEditing(true)} />;
};

export default CategoryTree;
